/**
 * English messages — the source dictionary. `hi.ts` must provide every key (enforced by its type).
 * Keys are grouped by area: `common.*`, `nav.*`, `sync.*`, `gate.*`, `store.*`, `auth.*` ...
 * Placeholders use {name} and are filled by `t(key, { name })`.
 */
import { storeEn } from './store.en'
import { v1PackEn } from './v1-pack.en'
import { v1GateProcessEn } from './v1-gate-process.en'
import { v1StockEn } from './v1-stock.en'
import { v1MoveCountEn } from './v1-move-count.en'
import { gateEn } from './gate.en'
import { processEn } from './process.en'
import { packEn } from './pack.en'
import { passportEn } from './passport.en'

export const en = {
  // F5 store phone flows (store.en.ts)
  ...storeEn,
  // V1 packing lots list and delabel (v1-pack.en.ts)
  ...v1PackEn,
  // V1 gate retag + processing machine label and issue (v1-gate-process.en.ts)
  ...v1GateProcessEn,
  // V1 godown stock lookup, red-tie bags, replace a torn bag (v1-stock.en.ts)
  ...v1StockEn,
  // V1 godown move bags + count a bay (v1-move-count.en.ts)
  ...v1MoveCountEn,
  // F4 gate phone flows (gate.en.ts)
  ...gateEn,
  // F6 processing site phone flows (process.en.ts)
  ...processEn,
  // F7 pack hall phone flows (pack.en.ts)
  ...packEn,
  // public Passport (passport.en.ts)
  ...passportEn,
  // common
  'common.appName': 'Uncompromised',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.continue': 'Continue',
  'common.done': 'Done',
  'common.retry': 'Try again',
  'common.save': 'Save',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'boot.stuck.title': 'The app is stuck opening',
  'boot.stuck.body':
    'Your work is safe on this phone. The browser has stopped answering the app. Do this:',
  'boot.stuck.step1': 'Close every other farmgate tab in this browser.',
  'boot.stuck.step2':
    'Close the browser fully (swipe it away from recent apps), then open farmgate again.',
  'boot.stuck.step3': 'Still stuck? Restart the phone.',
  'boot.stuck.warning': 'Do not clear browser data or history: unsent work would be lost.',
  'common.optional': 'optional',
  'common.required': 'required',
  'common.language': 'Language',
  'common.offline': 'Offline',
  'common.online': 'Online',
  'common.youAreOffline': 'You are offline. Work is saved on this phone and will sync later.',
  'common.comingSoon': 'This screen is being built.',
  'common.notFound': 'Page not found',
  'common.notFoundBody': 'The page you opened does not exist or has moved.',
  'common.goHome': 'Go home',
  'common.somethingWrong': 'Something went wrong',
  'common.somethingWrongBody': 'The screen hit an error. Your saved work is safe on this device.',
  'common.reload': 'Reload',
  'common.noResults': 'Nothing matches your search.',
  'common.page': 'Page {page} of {pages}',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.rows': '{count} {count:row|rows}',
  'common.step': 'Step {current} of {total}',

  // PWA
  'pwa.updateTitle': 'A new version is ready',
  'pwa.updateBody': 'Reload to use it. Unsynced work stays on the phone.',
  'pwa.updateAction': 'Reload',
  'pwa.offlineReady': 'Ready to work offline',
  'pwa.installTitle': 'Install on this phone',
  'pwa.installBody': 'Opens full screen and works without network.',
  'pwa.installAction': 'Install',

  // auth
  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'auth.identifier': 'Email, phone or username',
  'auth.password': 'Password',
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.signInHint': 'Use the account the office gave you. Sign-in needs network.',
  'auth.invalid': 'Wrong email/phone or password.',
  'auth.inactive': 'This account is not active. Ask the office.',
  'auth.needsNetwork': 'Sign-in needs network. Move to where the phone has signal.',
  'auth.forbidden': 'Your account cannot open this page.',
  'auth.sessionExpired': 'Your sign-in has expired. Please sign in again.',

  // PIN
  'pin.title': 'Unlock with PIN',
  'pin.who': 'Who is working?',
  'pin.enter': 'Enter your PIN',
  'pin.wrong': 'Wrong PIN. {left} {left:try|tries} left.',
  'pin.lockedOut': 'Too many wrong PINs. Wait {seconds} {seconds:second|seconds}.',
  'pin.noTeam': 'No team on this phone yet. Download the farmer list first.',
  'pin.noTeamSite': 'No team on this phone yet. Download the store list first.',
  'pin.noPinSet': 'No PIN is set for this person. Set it online first.',
  'pin.lock': 'Lock',
  'pin.switchUser': 'Switch person',
  'role.admin': 'Admin',
  'role.central_admin': 'Central admin',
  'role.supervisor': 'Supervisor',
  'role.village_operator': 'Purchase operator',
  'role.store_keeper': 'Store keeper',
  'role.machine_operator': 'Machine operator',
  'role.payments_approver': 'Payments approver',
  'role.quality_release': 'Quality release',
  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'Phone setting',
  'auth.eyebrow': 'Supply chain',
  'a11y.dismiss': 'Dismiss',
  'a11y.loading': 'Loading',
  'a11y.mainNav': 'Main menu',
  'pin.delete': 'Delete',
  'pin.clear': 'Clear',

  // device
  'device.title': 'Set up this phone',
  'device.body':
    'Sign in once, then tap Set up this phone: it joins your village and downloads the farmer list. After that, your PIN is all you need.',
  'device.uid': 'Phone code',
  'device.status': 'Registration',
  'device.registered': 'Registered',
  'device.demoOffline':
    'Demo phone: its lists come from the demo data and are not on the server. Load the demo data again to reset it.',
  'device.notRegistered': 'Not set up yet',
  'device.revoked': 'This phone has been switched off by the office. Data on it was cleared.',
  'device.check': 'Set up this phone',
  'device.pickVillage': 'Village for this phone',
  'device.pickVillageHint':
    'Which village is this phone for? Pick it below, then tap Set up this phone again.',
  'device.rosterLabel': 'Farmer list',
  'more.export': 'Export data (ZIP)',
  'more.exporting': 'Making the file…',
  'more.exported': 'Exported {records} records, {drafts} in progress, {photos} photos',
  'more.exportedUploaded': '{count} uploaded photos are already on the server, not in the file.',
  'more.exportFailed': 'Export failed',
  'device.downloadRoster': 'Update farmer list',
  'device.rosterAge': 'Farmer list downloaded {when}',
  'device.rosterNever': 'Farmer list not downloaded',
  'device.rosterStale': 'Farmer list is more than a day old.',
  'device.rosterExpired': 'Farmer list is too old to use. Update it with network.',
  'device.rosterUpdated': 'Farmer list updated',
  // the same, on a store (site) phone
  'device.storeListLabel': 'Store list',
  'device.downloadStoreList': 'Update store list',
  'device.storeListUpdated': 'Store list updated',
  'device.storeListAge': 'Store list downloaded {when}',
  'device.storeListNever': 'Store list not downloaded',
  'device.storeListStale': 'Store list is more than a day old.',
  'device.storeListExpired': 'Store list is too old to use. Update it with network.',
  'device.team': 'Team',
  'device.prefix': 'Code prefix',
  'device.devSeed': 'Load demo data',

  // sync
  'cmd.bind_machine_label': 'Machine label',
  'cmd.cancel_run': 'Run cancelled',
  'cmd.close_issue': 'Issue closed',
  'cmd.close_run': 'Run closed',
  'cmd.close_session': 'Day closed',
  'cmd.add_bank_account': 'Bank account (saved offline)',
  'cmd.commit_delivery': 'Purchase',
  'cmd.confirm_cleanout': 'Clean-out done',
  'cmd.confirm_roll': 'Roll confirmed',
  'cmd.correct_run': 'Run corrected',
  'cmd.decide_run': 'Run decided',
  'cmd.count_bay': 'Bay count',
  'cmd.dispatch_shipment': 'Truck sent',
  'cmd.issue_to_run': 'Issued to a run',
  'cmd.load_bags': 'Bags loaded',
  'cmd.mark_bags_conv': 'Bags marked red tie',
  'cmd.move_bags': 'Bags moved',
  'cmd.open_run': 'Run opened',
  'cmd.open_session': 'Day opened',
  'cmd.pack_close': 'Packing closed',
  'cmd.put_away': 'Put away',
  'cmd.receive_shipment': 'Truck received',
  'cmd.record_output': 'Output weighed',
  'cmd.replace_container': 'Bag replaced',
  'cmd.report_mixing': 'Mixing reported',
  'cmd.retag': 'New tag',
  'cmd.seal_sample': 'Sample sealed',
  'cmd.sign_bay_declaration': 'Bay declaration',
  'cmd.spoil_slip': 'Slip spoiled',
  'cmd.verify_pouch': 'Pouch checked',
  'cmd.void_delivery': 'Purchase cancelled',
  'cmd.void_output': 'Output cancelled',
  'cmd.void_tag': 'Tag cancelled',
  'cmd.weigh_in': 'Weighed in',
  'cmd.weigh_pouches': 'Pouches weighed',
  'outbox.PENDING': 'Waiting',
  'outbox.SENDING': 'Sending',
  'outbox.APPLIED': 'Sent',
  'outbox.DUPLICATE': 'Sent',
  'outbox.HELD': 'Held by office',
  'outbox.WAIT_FOR_PARENT': 'Waiting for earlier work',
  'outbox.ERROR': 'Needs a look',
  'sync.title': 'Sync',
  'sync.now': 'Sync now',
  'sync.pending': '{count} waiting',
  'sync.allSynced': 'All synced',
  'sync.syncing': 'Syncing…',
  'sync.lastSynced': 'Last synced {when}',
  'sync.never': 'Never synced',
  'sync.errors': '{count} {count:needs|need} attention',
  'sync.held': '{count} on hold at office',
  'sync.signInNeeded': 'Sign in again to send waiting work.',
  'sync.queueEmpty': 'Nothing waiting to send.',
  'sync.photos': '{count} {count:photo|photos} waiting',
  'sync.statPending': 'Waiting',
  'sync.statHeld': 'On hold',
  'sync.statErrors': 'Problems',
  'sync.statPhotos': 'Photos',
  'sync.statSent': 'Sent',
  'sync.showAll': 'Show everything',
  'sync.nothingHere': 'Nothing here.',
  'sync.exportAll': 'Download all data on this phone (ZIP)',
  'sync.exporting': 'Preparing the file…',
  'sync.exported': 'Saved: {records} records, {photos} photos',
  'sync.d.farmer': 'Farmer',
  'sync.d.crop': 'Crop',
  'sync.d.lot': 'Lot',
  'sync.d.bags': 'Bags',
  'sync.d.gross': 'Gross weight',
  'sync.d.net': 'Net weight',
  'sync.d.amount': 'Amount',
  'sync.d.moisture': 'Moisture',
  'sync.d.slip': 'Slip no.',
  'sync.d.reason': 'Reason',
  'sync.d.bagsShort': '{count} {count:bag|bags}',
  'sync.d.photos': 'Photos {sent}/{total} sent',
  'sync.d.reached': 'Reached the server {when}',
  'sync.d.heldWhy':
    'On hold at the office: {count} {count:issue|issues} for the office to clear. Nothing to do on the phone.',
  'sync.d.share': 'Send to the team',
  'sync.d.copied': 'Copied. Paste it in WhatsApp for the team.',
  'sync.d.allData': 'All data',
  'sync.d.hideData': 'Hide data',
  'sync.p.SENT': 'Sent',
  'sync.p.WAITING': 'Waiting',
  'sync.p.LOST': 'Lost on phone',
  'sync.p.MISSING': 'Not on phone',
  'sync.p.onServer': 'On the server',
  'sync.p.noCopy': 'No copy',
  'sync.p.lostHelp':
    'This phone can no longer read these photos (a phone storage fault). Tell the team; the record itself has reached the server.',
  'sync.p.lostStrip':
    '{count} {count:photo is|photos are} lost on this phone. Tap Photos to see which.',
  'sync.p.lostCount': '{count} lost',
  'sync.p.allSent': 'All photos have been sent.',
  'sync.photos.title': 'Photos waiting',
  'sync.photos.tries': '{count} tries',
  'sync.photos.noError': 'Not tried yet',
  'sync.photos.sendNow': 'Send photos now',
  'sync.photos.sending': 'Sending photos…',
  'sync.photos.result': '{sent} sent, {failed} not sent',
  'outbox.SET_ASIDE': 'Set aside',
  'sync.statSetAside': 'Set aside',
  'sync.setAside': 'Set aside',
  'sync.putBack': 'Put back',
  'sync.setAsideAsk':
    'Take “{what}” out of the queue so the rest can send. Nothing is deleted: it stays on this phone and the office sees it under Sync problems.',
  'sync.setAsideChildren':
    '{count} later {count:entry needs|entries need} it and will be set aside too.',
  'sync.setAsideDone': '{count} set aside',
  'sync.setAsideBy': 'Set aside by {name}, {when}',
  'sync.blocked': 'Waiting for the problem above to be fixed or set aside.',
  'sync.officeSees': 'The office sees this under Sync problems.',
  'sync.why.PARENT_NEVER_ARRIVED':
    'Waited a day for data from another phone or an earlier entry. Make sure that phone syncs. If it still waits, a supervisor can set this aside.',
  'sync.why.WAIT_FOR_PARENT':
    'Waiting for data from another phone or an earlier entry. Make sure that phone syncs.',
  'sync.why.SESSION_CODE_TAKEN':
    'This collection day code is already used on the server. Ask the office.',
  'sync.why.UNKNOWN_VILLAGE':
    'The server does not know this village. Ask the office to check this phone’s village.',
  'sync.why.ID_ALREADY_USED':
    'This entry’s id is already used by another record on the server. Ask the office.',
  'sync.why.SHIPMENT_CODE_TAKEN': 'This trip code is already used on the server. Ask the office.',
  'sync.why.PAYLOAD_LOCKED':
    'This entry is locked on the phone and cannot be read. Set it aside and tell the office.',
  'sync.why.COMMAND_CONFLICT':
    'The server already has this entry with different details. Ask the office.',

  // nav / phone home
  'nav.home': 'Home',
  'nav.sync': 'Sync',
  'nav.more': 'More',
  'home.greeting': 'Namaste, {name}',
  'home.gate': 'Collection point',
  'home.store': 'Store',

  // gate tiles (F4)
  'gate.openDay': 'Open collection day',
  'gate.openDayHint': 'Scale check, tag roll and seals',
  'gate.newDelivery': 'New delivery',
  'gate.newDeliveryHint': 'Find farmer, check, tag and weigh',
  'gate.closeDay': 'Close the day',
  'gate.closeDayHint': 'Count tags and seals',

  // store tiles (F5)
  'store.load': 'Load vehicle',
  'store.loadHint': 'Scan bags onto the vehicle',
  'store.receive': 'Receive',
  'store.receiveHint': 'Scan bags off at the godown',
  'store.putAway': 'Put away',
  'store.putAwayHint': 'Scan the place, then the bags',
  'store.move': 'Move',
  'store.moveHint': 'Move bags to another place',
  'store.count': 'Count a place',
  'store.countHint': 'Scan every bag in a bay',

  // scanner
  'scan.title': 'Scan code',
  'scan.tap': 'Tap to scan',
  'scan.torchOn': 'Torch on',
  'scan.torchOff': 'Torch off',
  'scan.manual': 'Type the code',
  'scan.manualHint': 'Use when the camera cannot read the tag.',
  'scan.noCamera': 'Camera not available. Type the code instead.',
  'scan.permission': 'Allow camera access to scan.',
  'scan.use': 'Use this code',

  // photo
  'photo.take': 'Take photo',
  'photo.retake': 'Retake',
  'photo.saved': 'Photo saved on phone',
  'photo.failed': 'Could not save the photo. Try again.',

  // fields
  'field.kg': 'kg',
  'field.select': 'Select',
  'field.invalidKg': 'Enter kg with up to 3 decimals, e.g. 20.050',
} as const

export type MessageKey = keyof typeof en
export type Dictionary = Record<MessageKey, string>
